import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import useNoodleApi from '@/hooks/useNoodleApi';
import { useIsUserInitialized, useUser } from '@/providers/Auth';
import { serializers, useLocalStorageState } from '@/providers/LocalStorageState';
import * as tsClient from '@/tsClient';
import { getCreatorWorkflowRoles } from '@/tsClient/creators';
import { type getTeams } from '@/tsClient/my';
import type * as ApiModels from '@/typings/api-models';

import { type Team, type TeamOwner } from './constants';
import TeamsContext from './TeamsContext';

const PAGE_SIZE = 300;
const PAGE = 1;

const TeamsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [user] = useUser();
  const isUserInitialized = useIsUserInitialized();
  const [teams, setTeams] = useState<Team[]>([]);
  const [workflowRoles, setWorkflowRoles] = useState<ApiModels.WorkflowRole[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [currentTeamOwner, setCurrentTeamOwner] = useState<TeamOwner | null>(null);
  const [currentRole, setCurrentRole] = useState({
    admin: false,
    finance: false,
    manageTeam: false,
    role: 'caseWorker' as ApiModels.TeamMemberRoles['role'],
  });
  const [creatorId, setCreatorId] = useLocalStorageState<string | null>({
    key: 'creatorId',
    prefix: 'teams',
    ...serializers.string,
  });

  const [currentTeamId, setCurrentTeamId] = useLocalStorageState<string | null>({
    key: 'currentTeamId',
    prefix: 'teams',
    ...serializers.string,
  });

  const [hasFetchedTeams, setHasFetchedTeams] = useState(false);
  const { data: fetchedTeams, getData: getTeamsForUser } = useNoodleApi(tsClient.my.getTeams);
  const { getData: getCreatorWorkflowRolesFn } = useNoodleApi(getCreatorWorkflowRoles);

  const clearTeamData = useCallback((): void => {
    setCurrentTeam(null);
    setCurrentTeamId(null);
    setCurrentTeamOwner(null);
    setCreatorId(null);
    setTeams([]);
    setHasFetchedTeams(true);
    setWorkflowRoles([]);
    setCurrentRole({
      admin: false,
      finance: false,
      manageTeam: false,
      role: 'caseWorker',
    });
  }, [setCreatorId, setCurrentTeamId]);

  const wrapSetCurrentTeam = useCallback(
    (newTeam: Team | null) => {
      if (!newTeam) {
        clearTeamData();
      } else {
        setCurrentTeam(newTeam);
        setCurrentTeamId(newTeam.id);
        setCurrentTeamOwner(newTeam.teamOwner);
        setCreatorId(newTeam.teamOwner.id);
        setCurrentRole(newTeam.role);
      }
    },
    [setCurrentRole, clearTeamData, setCurrentTeam, setCurrentTeamId, setCurrentTeamOwner, setCreatorId],
  );

  useEffect(() => {
    if (user && user?.id && !user.isAnonymous) {
      setHasFetchedTeams(false);
      getTeamsForUser({
        page: PAGE,
        perPage: PAGE_SIZE,
      });
    }
  }, [user, getTeamsForUser]);

  useEffect(() => {
    if (isUserInitialized && !user) {
      clearTeamData();
    }
  }, [clearTeamData, isUserInitialized, user]);

  const setTeamDataLatest = async (teamsArr: Team[]): Promise<void> => {
    setTeams(teamsArr);

    const storedTeam = teamsArr.find((team) => team.id === currentTeamId);
    const teamToSet: Team | null = storedTeam || (teamsArr.length > 0 && teamsArr[0]) || null;

    if (teamToSet) {
      wrapSetCurrentTeam(teamToSet);
    } else {
      clearTeamData();
    }
    setHasFetchedTeams(true);
  };
  const setTeamDataRef = useRef(setTeamDataLatest);
  setTeamDataRef.current = setTeamDataLatest;

  const refetchData = useCallback(async (): Promise<Awaited<ReturnType<typeof getTeams>>['items']> => {
    if (user && user?.id && !user.isAnonymous) {
      const { data } = await getTeamsForUser({ page: PAGE, perPage: PAGE_SIZE });
      setTeamDataRef.current(data?.items || []);
      return data?.items || [];
    }
    return [];
  }, [getTeamsForUser, user]);

  useEffect(() => {
    if (fetchedTeams) {
      if ((fetchedTeams?.items?.length || 0) > 0) {
        setTeamDataRef.current(fetchedTeams.items);
      } else {
        clearTeamData();
        setHasFetchedTeams(true);
      }
    }
  }, [fetchedTeams, clearTeamData]);

  const fetchWorkflowRoles = useCallback(async (): Promise<void> => {
    if (!creatorId) {
      return;
    }
    const { data } = await getCreatorWorkflowRolesFn({
      creatorId,
      page: 1,
      perPage: 1_000,
    });
    if (data?.items) {
      setWorkflowRoles(data.items);
    }
  }, [getCreatorWorkflowRolesFn, creatorId]);

  // Automatically fetch workflow roles when creatorId changes
  useEffect(() => {
    if (creatorId) {
      fetchWorkflowRoles();
    } else {
      setWorkflowRoles([]);
    }
  }, [creatorId, fetchWorkflowRoles]);

  const value = useMemo(
    () => ({
      creatorId,
      currentTeam,
      currentTeamOwner,
      fetchWorkflowRoles,
      hasFetchedTeams,
      refetchTeams: refetchData,
      role: currentRole,
      setCurrentTeam: wrapSetCurrentTeam,
      setWorkflowRoles,
      teams,
      workflowRoles,
    }),
    [
      hasFetchedTeams,
      wrapSetCurrentTeam,
      creatorId,
      currentRole,
      currentTeamOwner,
      currentTeam,
      teams,
      refetchData,
      workflowRoles,
      setWorkflowRoles,
      fetchWorkflowRoles,
    ],
  );

  return <TeamsContext.Provider value={value}>{children}</TeamsContext.Provider>;
};

export default TeamsProvider;
